import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  IQuestionAnswerResponse,
  IQuizAnswerSelection,
  IQuizOption,
  IQuizQuestion,
  IQuizResource,
  QuizAnswerRequest,
} from '../../models/WorkplaceQuiz'
import Conditional from '../common/Conditional'
import WorkplaceQuizModuleQuestionSelect from './WorkplaceQuizModuleQuestionSelect'
import {
  ApiResponse,
  createMarkupWithOption,
  IFormSubmission,
} from '../../models/Common'
import WorkplaceQuizContext from './context/WorkplaceQuizContext'
import WorkplaceQuizModuleQuestionFeedback from './WorkplaceQuizModuleQuestionFeedback'
import WorkplaceQuizService from '../../services/WorkplaceQuizService'
import WorkplaceQuizProgressbar from './WorkplaceQuizProgressbar'

export interface IWorkplaceQuizModuleQuestionProps {
  quizId: number
  introTitleContent: string
  question: IQuizQuestion
  index: number
  total: number
  resources: IQuizResource[]
  next: () => void
  back: () => void
  handleErrors?: (any) => void
  menuToggle: () => void
}

const WorkplaceQuizModuleQuestion = (
  props: IWorkplaceQuizModuleQuestionProps
) => {
  const mountedRef = useRef(false)
  const context = useContext(WorkplaceQuizContext)
  const [isLoading, setIsLoading] = useState(true)
  const [question, setQuestion] = useState<IQuizQuestion>()
  const [form, setForm] = useState<IFormSubmission>()
  const [, setResources] = useState<IQuizResource[]>()
  const [answer, setAnswer] = useState<IQuizAnswerSelection>({
    options: [],
    showFeedback: false,
    answeredCorrect: false,
    almostCorrect: false,
    maybeAnsweredCorrect: false,
    retryAnswer: false,
  })

  const next = async () => {
    //Check if the feedback is shown
    if (answer.showFeedback) {
      props.next()
      return
    }

    setForm((prevState) => ({
      ...prevState,
      submitting: true,
    }))

    //Check the answer from the API before moving next.
    const payload = new QuizAnswerRequest()
    payload.quizId = props.quizId
    payload.textId = question.textId
    payload.selection = answer.options.map((item) => {
      return item.textId
    })

    await WorkplaceQuizService.checkAnswer(payload)
      .then((response: ApiResponse<IQuestionAnswerResponse>) => {
        context.updateAnswer(
          props.quizId,
          question,
          response.result.isCorrect,
          answer.options,
          !response.result.isCorrect && question.failureResourcesContent
            ? question.failureResourcesContent
            : []
        )

        setAnswer((prevState) => ({
          ...prevState,
          showFeedback: true,
          answeredCorrect: response.result.isCorrect,
          maybeAnsweredCorrect: response.result.isMayBe,
          almostCorrect: response.result.isAlmostCorrect,
          retryAnswer: false,
        }))

        question.options?.map((item) => {
          item.selected = answer.options.findIndex((p) => p.id === item.id) >= 0
        })
        setQuestion(props.question)
      })
      .catch((error) => {
        props.handleErrors(error)
      })
      .finally(() => {
        setForm((prevState) => ({
          ...prevState,
          submitting: false,
        }))
      })
  }
  const onKeyPressNextSelect = async (event) => {
    //Check if the feedback is shown
    if (event.keyCode === 13) {
      if (answer.showFeedback) {
        props.next()
        return
      }

      setForm((prevState) => ({
        ...prevState,
        submitting: true,
      }))

      //Check the answer from the API before moving next.
      const payload = new QuizAnswerRequest()
      payload.quizId = props.quizId
      payload.textId = question.textId
      payload.selection = answer.options.map((item) => {
        return item.textId
      })

      await WorkplaceQuizService.checkAnswer(payload)
        .then((response: ApiResponse<IQuestionAnswerResponse>) => {
          context.updateAnswer(
            props.quizId,
            question,
            response.result.isCorrect,
            answer.options,
            !response.result.isCorrect && question.failureResourcesContent
              ? question.failureResourcesContent
              : []
          )

          setAnswer((prevState) => ({
            ...prevState,
            showFeedback: true,
            answeredCorrect: response.result.isCorrect,
            maybeAnsweredCorrect: response.result.isMayBe,
            almostCorrect: response.result.isAlmostCorrect,
            retryAnswer: false,
          }))

          question.options?.map((item) => {
            item.selected =
              answer.options.findIndex((p) => p.id === item.id) >= 0
          })
          setQuestion(props.question)
        })
        .catch((error) => {
          props.handleErrors(error)
        })
        .finally(() => {
          setForm((prevState) => ({
            ...prevState,
            submitting: false,
          }))
        })
    }
  }
  const onKeyPressHandler = (event) => {
    if (event.keyCode === 13) {
      props.menuToggle()
    }
  }
  const onKeyPressHandlerBack = (event) => {
    if (event.keyCode === 13) {
      //Check if the feedback is shown
      if (answer.showFeedback) {
        setAnswer((prevState) => ({
          ...prevState,
          showFeedback: false,
          maybeAnsweredCorrect: false,
          retryAnswer: false,
          almostCorrect: false,
        }))
        return
      }

      props.back()
    }
  }
  const back = () => {
    //Check if the feedback is shown
    if (answer.showFeedback) {
      setAnswer((prevState) => ({
        ...prevState,
        showFeedback: false,
        maybeAnsweredCorrect: false,
        retryAnswer: false,
        almostCorrect: false,
      }))
      return
    }

    props.back()
  }

  const selectionChange = (options: IQuizOption[]) => {
    setAnswer((prevState) => ({
      ...prevState,
      options: options,
    }))
  }

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      props.question?.options.map((item) => {
        item.ref = createRef() //This way we get a ref to dynamic content
        item.multiselect = props.question.checkboxes
        item.selected =
          context.currentState.answers.length > 0 &&
          context.currentState.answers.findIndex(
            (p) =>
              p.id === item.id &&
              p.quizId === item.quizId &&
              p.questionId === item.questionId
          ) >= 0
      })
      setQuestion(props.question)
      setResources(props.resources)
      setAnswer({
        options: props.question?.options.filter((p) => p.selected === true),
        showFeedback: false,
        answeredCorrect: false,
        almostCorrect: false,
        maybeAnsweredCorrect: false,
        retryAnswer: false,
      })
      setForm((prevState) => ({
        ...prevState,
        submitting: false,
      }))
      setIsLoading(false)
    }
    return () => {
      mountedRef.current = false
    }
  }, [props.question])

  return (
    <Conditional if={!isLoading}>
      <div className="question">
        <div className="barHeaderQuestions">
          <div
            className="quickMenuArea"
            onClick={props.menuToggle}
            role="button"
            tabIndex={0}
            onKeyDown={onKeyPressHandler}
          >
            <a className="quickMenu">
              <img
                alt="Quick Menu"
                src="/public/workplace/assets/images/button/clear/quickmenu_normal.png"
              />
              <span>Quick Menu - show/hide above.</span>
            </a>
          </div>
          <div className="title">
            <h2 className="titleContent">
              Workplace <span>Basics</span>
            </h2>
          </div>
          <WorkplaceQuizProgressbar
            title={props.introTitleContent}
            current={props.index}
            total={props.total}
            showNumber={true}
          />
        </div>

        <div className="questionArea">
          <div className="imageArea">
            <img
              className="image"
              src={'/public/' + question?.image}
              alt={question?.altContent}
            />
          </div>
          <div className="content">
            <div className="contentHolder">
              <Conditional if={!answer.showFeedback}>
                <div className="holder">
                  <div
                    id="toph"
                    className="questionText"
                    dangerouslySetInnerHTML={createMarkupWithOption(
                      question?.content,
                      { ADD_ATTR: ['target'] }
                    )}
                  ></div>
                </div>
                <WorkplaceQuizModuleQuestionSelect
                  question={question}
                  totalSelected={answer?.options.length}
                  onSelectionChange={selectionChange}
                />
              </Conditional>
              <Conditional if={answer.showFeedback}>
                <div className="feedbackArea">
                  <div className="contentHolder">
                    <WorkplaceQuizModuleQuestionFeedback
                      question={question}
                      answeredCorrect={answer.answeredCorrect}
                      maybeAnsweredCorrect={answer.maybeAnsweredCorrect}
                      retryAnswer={answer.retryAnswer}
                      almostCorrect={answer.almostCorrect}
                    />
                  </div>
                </div>
              </Conditional>

              <div className="navButtons">
                <a
                  className={
                    form?.submitting
                      ? 'button is-secondary disabled'
                      : 'button is-secondary clickable'
                  }
                  onClick={() => back()}
                  onKeyDown={onKeyPressHandlerBack}
                  role="button"
                  tabIndex={0}
                >
                  Back
                </a>
                <a
                  className={
                    form?.submitting ||
                    answer.options.length <= 0 ||
                    answer.options.length !== question.maxCorrect
                      ? 'button is-primary disabled'
                      : 'button is-primary clickable'
                  }
                  onClick={
                    answer.options.length <= 0 ||
                    answer.options.length !== question.maxCorrect
                      ? null
                      : () => next()
                  }
                  onKeyDown={onKeyPressNextSelect}
                  role="button"
                  tabIndex={0}
                >
                  {answer.showFeedback ? 'Next' : 'Select'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Conditional>
  )
}

export default WorkplaceQuizModuleQuestion
